import { useEffect, useRef, useState, type ReactNode } from 'react';
import { store } from '../../state/store';
import { useStore } from '../useStore';
import { Icon } from '../Icon';
import { MaterialImage } from './MaterialPicker';
import { NO_WINDOW_VIEW, WINDOW_VIEWS, normaliseViewRotation, windowViewById, windowViewFiles } from '../../state/windowViews';
import { t } from '../../i18n/I18n';

/** The picker's step buttons turn a view by this much; the slider and the arrow keys by a degree. */
const ROTATION_STEP = 15;

/**
 * «Вид из окна»: the view out of the windows for the whole project, as the app's «Выбери вид» chooses the one behind
 * a window (Unity StreetViewPopup), and how far it is turned. A choice shows in the 3D view at once — the old view
 * stays until the new one's preview is in — and is kept with where the project was left.
 */
export function WindowViewPicker() {
  const state = useStore(s => ({ view: s.windowView, rotation: s.windowViewRotation, status: s.windowViewStatus }));
  const [closing, setClosing] = useState(false);
  const sheetRef = useRef<HTMLElement>(null), closeTimer = useRef<number | undefined>(undefined);
  // Focus goes back where it was when the sheet closes: the menu's trigger, or the context bar.
  const opener = useRef(typeof document === 'undefined' ? null : document.activeElement as HTMLElement | null);
  const chosen = windowViewById(state.view), none = state.view === NO_WINDOW_VIEW;
  useEffect(() => {
    sheetRef.current?.querySelector<HTMLButtonElement>('.swatch[aria-pressed="true"]')?.focus({ preventScroll: true });
    return () => clearTimeout(closeTimer.current);
  }, []);
  const close = () => {
    if (closing) return;
    const finish = () => {
      store.set({ showWindowViews: false });
      if (opener.current?.isConnected) opener.current.focus({ preventScroll: true });
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else { setClosing(true); closeTimer.current = window.setTimeout(finish, 160); }
  };
  const choose = (id: string) => { if (id !== state.view) store.set({ windowView: id }); };
  const turn = (value: number) => store.set({ windowViewRotation: normaliseViewRotation(value) });
  const loading = !none && state.status === 'loading', failed = !none && state.status === 'error';
  const tile = (id: string, title: string, picture: ReactNode) => {
    const selected = state.view === id;
    return <button key={id} type="button" className="swatch window-view-tile" aria-pressed={selected} aria-busy={selected && loading} title={title} aria-label={title} onClick={() => choose(id)}>
      <span className="swatch-picture">{picture}{selected && loading && <span className="swatch-busy" aria-hidden="true" />}
        {selected && failed && <span className="swatch-alert" aria-hidden="true">!</span>}
        {selected && !loading && !failed && <span className="swatch-check"><Icon name="check" size={16} /></span>}</span>
      <span className="window-view-caption">{title}</span>
    </button>;
  };
  return <div className="material-backdrop" onClick={close}>
    <section ref={sheetRef} className={'material-sheet window-view-sheet at-bottom' + (closing ? ' is-closing' : '')} inert={closing} role="dialog" aria-modal="true" aria-label={t('#WindowViewTitle')} onClick={event => event.stopPropagation()} onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
      if (event.key !== 'Tab') return;
      const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)')].filter(control => !control.closest('[inert]'));
      const next = event.shiftKey ? controls.at(-1) : controls[0];
      if ((event.shiftKey && document.activeElement === controls[0]) || (!event.shiftKey && document.activeElement === controls.at(-1))) { event.preventDefault(); next?.focus(); }
    }}>
      <header className="material-head"><button type="button" className="icon-btn" aria-label={t('#WindowViewClose')} onClick={close}><Icon name="close" size={20} /></button><h2>{t('#WindowViewTitle')}</h2><span className="material-head-spacer" /></header>
      <div className="swatches window-views">
        {tile(NO_WINDOW_VIEW, t('#WindowViewNone'), <span className="window-view-none" aria-hidden="true"><Icon name="close" size={22} /></span>)}
        {WINDOW_VIEWS.map(view => tile(view.id, view.title, <MaterialImage src={windowViewFiles(view).thumb} />))}
      </div>
      <p className={'window-view-status' + (failed ? ' is-error' : '')} role={failed ? 'alert' : 'status'}>
        {failed ? <>{t('#WindowViewLoadFailed', chosen?.title ?? '')} <button type="button" className="text-button" onClick={() => window.dispatchEvent(new CustomEvent('hp:retry-window-view'))}>{t('#PurchaseRetryLoad')}</button></>
          : loading ? t('#WindowViewLoading', chosen?.title ?? '')
          : none ? t('#WindowViewNoneNote')
          : t('#WindowViewSunNote')}
      </p>
      <div className="window-view-turn" aria-disabled={none} inert={none}>
        <Icon name="rotate" size={20} />
        <span className="texture-control-title">{t('#WindowViewRotation')}</span>
        <input className="texture-slider" type="range" min={0} max={359} step={1} value={state.rotation} aria-label={t('#WindowViewRotation')} aria-valuetext={`${state.rotation}°`} onChange={event => turn(Number(event.target.value))} />
        <span className="window-view-steps">
          <button type="button" className="icon-btn" aria-label={t('#WindowViewTurnLeft', ROTATION_STEP)} title={t('#WindowViewTurnLeft', ROTATION_STEP)} onClick={() => turn(state.rotation - ROTATION_STEP)}><Icon name="back" size={16} /></button>
          <span className="window-view-degrees">{state.rotation}°</span>
          <button type="button" className="icon-btn" aria-label={t('#WindowViewTurnRight', ROTATION_STEP)} title={t('#WindowViewTurnRight', ROTATION_STEP)} onClick={() => turn(state.rotation + ROTATION_STEP)}><Icon name="next" size={16} /></button>
        </span>
      </div>
    </section>
  </div>;
}
